import React, { useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { SiteCamerasResult } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { SiteWallCamerasStatus } from "./useSiteWallCameras";
import {
  MINI_WALL_PAGE_SIZE,
  VIDEO_FRAME_CLASS,
} from "./siteVideoWallConstants";
import { SiteVideoWallPagination } from "./SiteVideoWallPagination";
import { SiteVideoWallTile } from "./SiteVideoWallTile";
import { WallIdlePrompt } from "./WallStreams";

const theme = createTheme({
  palette: {
    primary: {
      main: "#FF8400",
    },
  },
});

interface SiteVideoWallMiniPanelProps {
  open: boolean;
  onClose: () => void;
  onExpand: () => void;
  /** When set, expand is an <a> so right-click / middle-click can open in a new tab. */
  expandHref?: string;
  status: SiteWallCamerasStatus;
  cameras: SiteCamerasResult[];
  totalCount: number;
  page: number;
  onPageChange: (page: number) => void;
  ensureCamerasLoaded: (upToIndex: number) => Promise<void>;
}

export const SiteVideoWallMiniPanel: React.FC<SiteVideoWallMiniPanelProps> = ({
  open,
  onClose,
  onExpand,
  expandHref,
  status,
  cameras,
  totalCount,
  page,
  onPageChange,
  ensureCamerasLoaded,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / MINI_WALL_PAGE_SIZE));
  const startIndex = page * MINI_WALL_PAGE_SIZE;
  const endIndex = startIndex + MINI_WALL_PAGE_SIZE - 1;

  useEffect(() => {
    if (!open || status !== "ready") return;
    ensureCamerasLoaded(endIndex);
  }, [open, status, page, endIndex, ensureCamerasLoaded]);

  if (!open) return null;

  const visibleCameras = cameras.slice(startIndex, startIndex + MINI_WALL_PAGE_SIZE);
  const placeholderCount = Math.max(
    0,
    Math.min(MINI_WALL_PAGE_SIZE, totalCount - startIndex) - visibleCameras.length
  );

  return (
    <div
      className="absolute bottom-14 right-3.5 z-20 flex max-w-[70%] flex-col gap-2 rounded border border-actuate-grey bg-white p-2.5 font-mulish shadow-sm"
      style={{ width: 430 }}
      data-testid="site-video-wall-mini-panel"
    >
      <div className="flex items-center gap-2 text-[14px] font-extrabold uppercase tracking-[0.5px] text-actuate-blue">
        <span>
          Site Wall — {totalCount} Camera{totalCount === 1 ? "" : "s"}
        </span>
        <IconButton
          aria-label="Close site wall"
          onClick={onClose}
          size="small"
          sx={{ marginLeft: "auto", color: "#001943", padding: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {status === "loading" && (
        <div className="flex flex-col items-center justify-center py-8">
          <ThemeProvider theme={theme}>
            <CircularProgress color="primary" size={28} />
          </ThemeProvider>
          <p className="mt-3 text-[12px] text-gray-500">
            Retrieving cameras...
          </p>
        </div>
      )}

      {status === "error" && (
        <p className="py-6 text-center text-[12px] text-gray-500">
          Unable to load cameras for this site.
        </p>
      )}

      {status === "ready" && totalCount === 0 && (
        <p className="py-6 text-center text-[12px] text-gray-500">
          No cameras found for this site.
        </p>
      )}

      {status === "ready" && totalCount > 0 && (
        <>
          <WallIdlePrompt compact />
          <div className="grid grid-cols-3 gap-1.5">
            {visibleCameras.map((camera) => (
              <SiteVideoWallTile
                key={camera.id}
                cameraId={String(camera.id)}
                cameraName={camera.camera_name}
                enabled={open}
              />
            ))}
            {Array.from({ length: placeholderCount }).map((_, i) => (
              <div
                key={`placeholder-${i}`}
                className={`flex aspect-[4/3] w-full items-center justify-center self-start ${VIDEO_FRAME_CLASS} border-transparent`}
              >
                <ThemeProvider theme={theme}>
                  <CircularProgress color="primary" size={16} />
                </ThemeProvider>
              </div>
            ))}
          </div>

          <SiteVideoWallPagination
            page={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
            variant="light"
            size="large"
          />

          {expandHref ? (
            <a
              href={expandHref}
              onClick={(e) => {
                // Plain left-click keeps in-page expand; modifiers / middle-click
                // and right-click "Open in new tab" use the real href.
                if (
                  e.metaKey ||
                  e.ctrlKey ||
                  e.shiftKey ||
                  e.altKey ||
                  e.button !== 0
                ) {
                  return;
                }
                e.preventDefault();
                onExpand();
              }}
              className="shadow-button self-center rounded border-2 border-actuate-orange bg-actuate-orange px-3 py-1.5 font-mulish text-base text-white no-underline hover:bg-[#E67300]"
            >
              Expand to full wall
            </a>
          ) : (
            <button
              type="button"
              onClick={onExpand}
              className="shadow-button self-center rounded border-2 border-actuate-orange bg-actuate-orange px-3 py-1.5 font-mulish text-base text-white hover:bg-[#E67300]"
            >
              Expand to full wall
            </button>
          )}
        </>
      )}
    </div>
  );
};
